import { useEffect, useRef, useState } from 'react';
import { FilterItem, RemoveFilter } from './types';

export const parseFilters = (filters: FilterItem[]) =>
  filters.filter((filter) =>
    Array.isArray(filter.value) ? !!filter.value.length : !!filter.value
  );

export const useFiltersState = (
  initialFilters: FilterItem[] | (() => FilterItem[]),
  onChange: (filters: FilterItem[]) => void
) => {
  const [filtersState, setFiltersState] = useState<FilterItem[]>(initialFilters);

  const initialFiltersRef = useRef(filtersState);
  const filtersStateRef = useRef(filtersState);

  useEffect(() => {
    filtersStateRef.current = filtersState;
  }, [filtersState]);

  const appliedFilters = parseFilters(filtersState);

  const removeFilter: RemoveFilter = (name, nextValue, options) => {
    const value = nextValue ?? null;
    const source = options?.fromInitialState ? initialFiltersRef.current : filtersStateRef.current;

    onChange(
      parseFilters(source.map((filter) => (filter.name === name ? { ...filter, value } : filter)))
    );
    setFiltersState((prevState) =>
      prevState.map((filter) => (filter.name === name ? { ...filter, value } : filter))
    );
  };

  const updateFilters = (filters: FilterItem[]) => {
    onChange(parseFilters(filters));

    setFiltersState((prevState) =>
      prevState.map((filter) => {
        const updatedFilter = filters.find((f) => f.name === filter.name);

        if (!updatedFilter) {
          return filter;
        }

        return {
          ...filter,
          ...updatedFilter,
          group: {
            ...filter.group,
            ...updatedFilter.group,
          },
        };
      })
    );
  };

  const clearAll = () => {
    setFiltersState((prevState) => prevState.map((filter) => ({ ...filter, value: null })));
    onChange(parseFilters(filtersState.map((filter) => ({ ...filter, value: null }))));
  };

  return {
    filtersState,
    setFiltersState,
    appliedFilters,
    removeFilter,
    updateFilters,
    clearAll,
  };
};
